import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Surrogate } from '../types.ts';
import { executives } from '../data/executives.ts';
import GradientText from '../../../components/common/GradientText.tsx';

interface ExecutiveBlocksProps {
  surrogates: Surrogate[];
}

const BlocksContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  width: '100%',
  maxWidth: '1200px',
  justifyContent: 'center',
  position: 'relative',
  marginLeft: 'auto',
  marginRight: 'auto',
}));

const Block = styled(motion.div)(({ theme }) => ({
  width: '120px',
  height: '120px',
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },

  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.4)',
    
    '&::before': {
      opacity: 1,
    },
  },
}));

const Initials = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 'bold',
  color: '#fff',
  textShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
}));

const Role = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: 'rgba(255, 255, 255, 0.7)',
  textAlign: 'center',
  marginTop: theme.spacing(1),
}));

const ExecutiveBlocks: React.FC<ExecutiveBlocksProps> = ({ surrogates }) => {
  const navigate = useNavigate();

  const handleBlockClick = (execId: string) => {
    navigate(`/executive/${execId}`);
  };

  return (
    <BlocksContainer>
      {executives.map((exec) => (
        <Box key={exec.id} sx={{ position: 'relative' }}>
          <Block
            onClick={() => handleBlockClick(exec.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              background: exec.gradient,
            }}
          >
            <Initials>{exec.initials}</Initials>
            <Role>{exec.title}</Role>
          </Block>
        </Box>
      ))}
      <GradientText variant="h6" gradient="secondary" sx={{ mb: 4, textAlign: 'center' }}>Eterna ExecOS: <br />The Executive Operating System <br /> Select Executive Surrogates</GradientText>
    </BlocksContainer>
  );
};

export default ExecutiveBlocks; 